import type { IntlShape } from 'react-intl';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { formatMmAsCmAtScale } from '~/utils/units';

/** What an image row says about an image that is not loaded. */
export interface UnloadedImageSummary {
    /** The series must be reformatted before it can be shown. */
    readonly needsReformat: boolean;
    /** The series description. PHI: mask wherever rendered. */
    readonly description: string;
    readonly sliceCount?: number;
    /** The in-plane pixel spacing in cm, as a label. */
    readonly spacingLabel?: string;
    /** The series date. PHI: mask wherever rendered. */
    readonly seriesDate?: Date;
}

/**
 * The summary of an image with no `Image3D`: a raw series that must be
 * reformatted, from its DICOM, or an image known only from the session,
 * from the session's geometry.
 */
export function describeUnloadedImage(
    image: AvailableImage,
    intl: IntlShape
): UnloadedImageSummary {
    const raw = image.rawSeries;
    if (raw) {
        const firstSlice = raw.dicomImageSlices[0];
        return {
            needsReformat: true,
            description:
                raw.seriesModule.seriesDescription ||
                intl.formatMessage({ id: 'unknown.series' }),
            sliceCount: raw.numberOfObjects,
            spacingLabel: firstSlice
                ? intl.formatMessage(
                      { id: 'image.spacingValue' },
                      {
                          spacing: Array.from(firstSlice.pixelSpacing)
                              .map((s) => formatMmAsCmAtScale(s, 'voxel'))
                              .join(' x '),
                      }
                  )
                : undefined,
            seriesDate: raw.seriesModule.seriesDate,
        };
    }
    const info = image.sessionImageInfo;
    return {
        needsReformat: false,
        description:
            image.label || intl.formatMessage({ id: 'unknown.series' }),
        sliceCount: image.sliceCount,
        spacingLabel: info
            ? intl.formatMessage(
                  { id: 'image.spacingValue' },
                  {
                      spacing: `${formatMmAsCmAtScale(
                          info.xResMm,
                          'voxel'
                      )} x ${formatMmAsCmAtScale(info.yResMm, 'voxel')}`,
                  }
              )
            : undefined,
        seriesDate: info?.date,
    };
}
